import config from '../../config/config';

import deleteRequest from './base/delete';
import getRequest from './base/get';
import patchRequest from './base/patch';
import postRequest from './base/post';

interface QueryParameters {
  limit?: number;
  [key: string]: any;
}

export default {
  /**
   * Fetch the Accounts list with pagination
   * @param {any} query query
   * @returns {any} promise
   */
  fetchAccountsFee: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query, limit: query?.limit || 5 };
    return getRequest(
      `${config.subscription.accountApiUrl}api/cpo/v1/facility-account/fee-config/account-list`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch the All Accounts list
   * @returns {any} promise
   */
  fetchAllAccounts: (): Promise<any> => {
    return getRequest(
      `${config.subscription.accountApiUrl}api/cpo/v1/facility-account/list-account-ids`,
      {},
    );
  },

  /**
   * Fetch single Accounts list with pagination
   * @param {any} query query
   * @returns {any} promise
   */
  fetchOneAccounts: (query: string): Promise<any> => {
    return getRequest(`${config.subscription.accountApiUrl}api/cpo/v1/facility-user/${query}`, {});
  },

  /**
   * Fetch partner summary data by partner ID
   * @param {string} id - Partner ID
   * @returns {Promise<any>} Promise containing partner summary data
   */
  fetchPartnerSummary: (id: string): Promise<any> => {
    return getRequest(`${config.commonApiUrl}api/cpo/v1/partner/partner-summary/${id}`, {});
  },
  /**
   * create the Accounts list with pagination
   * @param {any} data data
   * @returns {any} promise
   */
  createAccounts: (data: any): Promise<any> => {
    const parameters = { ...data };
    return postRequest(
      `${config.subscription.accountApiUrl}api/cpo/v1/facility-account/create-fo`,
      parameters,
      true,
    );
  },
  /**
   * Edit the account function
   * @param {any} id - Account ID
   * @param {any} data - Updated account payload
   * @returns {any} Promise containing updated account response
   */
  editAccounts: (id: string, data: any): Promise<any> => {
    const parameters = { ...data };
    return patchRequest(
      `${config.subscription.accountApiUrl}api/cpo/v1/facility-account/edit-account/${id}`,
      { ...parameters },
      true,
    );
  },

  /**
   * Approved password reset function
   * @param {any} id - The facility user ID
   * @param {any} data - The payload (e.g., { newPassword: 'yourPassword' })
   * @returns {any} Promise<any>
   */
  facilityRestPassword: (id: string, data: any): Promise<any> => {
    return patchRequest(
      `${config.subscription.accountApiUrl}api/cpo/v1/facility-user/reset-password/${id}`,
      data,
    );
  },

  /**
   * Approved password reset function
   * @param {any} id - The facility user ID
   * @param {any} data - The payload (e.g., { newPassword: 'yourPassword' })
   * @returns {any} Promise<any>
   */
  utilityRestPassword: (id: string, data: any): Promise<any> => {
    return patchRequest(
      `${config.subscription.accountApiUrl}api/cpo/v1/utility-provider/reset-password/${id}`,
      data,
    );
  },

  /**
   * Delete the account api call function
   * @param {any} id - Account ID
   * @returns {any} Promise containing delete response
   */
  deleteAccount: (id: string): Promise<any> => {
    return deleteRequest(
      `${config.subscription.accountApiUrl}api/cpo/v1/facility-account/delete-account/${id}`,
    );
  },

  /**
   * show for the Revenue and Admin share and facility share data
   * @param {any} query - Revenue filter query parameters
   * @returns {any} Promise containing consolidated revenue data
   */
  fetchConsolidatedRevenue: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.subscription.accountApiUrl}api/cpo/v1/facility-account/consolidated-revenue`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch single Accounts list with pagination
   * @param {any} id id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchFacilityAccount: (id: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.subscription.accountApiUrl}api/cpo/v1/facility-account/facility-account/${id}`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch Power Consumed Graph Data Reports
   * @param {any} id id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchPowerConsumedGraph: (id: string, query: QueryParameters): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-station/paid-charging-transaction/${id}`,
      { ...parameters },
    );
  },
  /**
   * Fetch Revenue Payable Graph Data Reports
   * @param {any} id id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchRevenuePayable: (id: string, query: QueryParameters): Promise<any> => {
    const parameters = { ...query };
    return getRequest(`${config.reportApiUrl}api/cpo/v1/revenue-payout-master/${id}`, {
      ...parameters,
    });
  },
  /**
   * Fetch Device Count Report
   * @param {any} id id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchDeviceCountReport: (id: string, query: QueryParameters): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-point/device-count-report-by-facility/${id}`,
      { ...parameters },
    );
  },
  /**
   * Fetch Device Table Report
   * @param {any} id id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchDeviceListReport: (id: string, query: QueryParameters): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-point/device-list-by-facility/${id}`,
      { ...parameters },
    );
  },
  /**
   * Fetch Device Table Report
   * @param {any} id id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchStationListReport: (id: string, query: QueryParameters): Promise<any> => {
    const parameters = { ...query };
    return getRequest(`${config.reportApiUrl}api/cpo/v1/charging-station//device-count/${id}`, {
      ...parameters,
    });
  },
  /**
   * Create Revenue Paout History
   * @param {any} id id
   * @param {any} data data
   * @returns {any} promise
   */
  createRevenuePayoutHistory: (id: string, data: any): Promise<any> => {
    const parameters = { ...data };
    return postRequest(
      `${config.transactionApiUrl}api/cpo/v1/revenue-payout-history/create/${id}`,
      parameters,
    );
  },
  /**
   * Create Revenue Payout Stripe
   * @param {any} id id
   * @param {any} data data
   * @returns {any} promise
   */
  createRevenuePayoutStripe: (id: string, data: any): Promise<any> => {
    const parameters = { ...data };
    return postRequest(
      `${config.transactionApiUrl}api/cpo/v1/revenue-payout/transfer/amount/${id}`,
      parameters,
    );
  },
  /**
   * Fetch Payout List Reports
   * @param {any} id id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchPayoutListReports: (id: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(`${config.transactionApiUrl}api/cpo/v1/revenue-payout-history/${id}`, {
      ...parameters,
    });
  },
  /**
   * Fetch Payout Export Data
   * @param {any} id id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchPayoutExportData: (id: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.transactionApiUrl}api/cpo/v1/revenue-payout-history/export-data/${id}`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch Payout List Filters
   * @returns {any} promise
   */
  fetchPayoutListFilters: (): Promise<any> => {
    return getRequest(
      `${config.transactionApiUrl}api/cpo/v1/revenue-payout-history/get/list-filters`,
    );
  },

  /**
   * Fetch Revenue By Transaction List Reports
   * @param {any} query query
   * @returns {any} promise
   */
  fetchRevenueByTransactionList: (query: QueryParameters = {}): Promise<any> => {
    const sn = query?.chargingPointSn || query?.chargingpointsn;
    const accountCustomIdValue = query?.accountCustomId;
    const station = query?.stationId || query?.chargingStation;

    const parameters: Record<string, any> = { ...query };
    delete parameters.chargingpointsn;
    delete parameters.chargingPointSn;
    delete parameters.accountCustomId;
    delete parameters.chargingStation;
    delete parameters.stationId;

    if (sn) {
      parameters.chargingPointSn = Array.isArray(sn) ? sn : [sn];
    }
    if (accountCustomIdValue) {
      parameters.accountCustomId = Array.isArray(accountCustomIdValue)
        ? accountCustomIdValue
        : [accountCustomIdValue];
    }
    if (station) {
      parameters.stationId = Array.isArray(station) ? station : [station];
    }

    return getRequest(
      `${config.cpo.transactionApiUrl}api/common/v1/charging-transaction/transaction-list`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch Revenue By Transaction Export Data
   * @param {any} query query
   * @returns {any} promise
   */
  fetchByTransactionExportData: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(`${config.reportApiUrl}api/cpo/v1/charging-transaction/export`, {
      ...parameters,
    });
  },

  /**
   * Fetch Revenue By Transaction List Filters
   * @returns {any} promise
   */
  fetchByTransactionListFilters: (): Promise<any> => {
    return getRequest(
      `${config.cpo.transactionApiUrl}api/common/v1/charging-transaction/transaction-list/filter-list`,
    );
  },
  /**
   * Fetch Revenue By Month List Reports
   * @param {any} id id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchRevenueByMonthList: (id: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-transaction-revenue/by-month/${id}`,
      {
        ...parameters,
      },
    );
  },
  /**
   * Fetch Revenue By Month Export Data
   * @param {any} id id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchByMonthExportData: (id: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.reportApiUrl}api/cpo/v1/charging-transaction-revenue/by-month/export/${id}`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetch Account Details data show
   * @param {any} id id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchAccountDetails: (id: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(`${config.reportApiUrl}api/cpo/v1/account/${id}`, {
      ...parameters,
    });
  },
  /**
   * Fetch Account Details data show
   * @param {any} id id
   * @param {any} query query
   * @returns {any} promise
   */
  fetchAccountRevenueTotal: (id: string, query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query };
    return getRequest(
      `${config.transactionApiUrl}api/cpo/v1/revenue-payout-master/account-revenue/${id}`,
      {
        ...parameters,
      },
    );
  },
};
